import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Container } from "@mui/material";
import logoImage from "../../assets/images/logo.png";
import callApi from "../../services/callApi";
import Loader from "../../components/Loader/Loader";
import ReferralBanner from "./components/ReferralBanner";
import { leerBaul } from "../../services/referralBaul";

// ─── Estilos compartidos ─────────────────────────────────────────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const inputSx = {
  mb: 1,
  "& .MuiInputBase-root": {
    height: { xs: "44px", md: "46px" },
    backgroundColor: "#f7f9fc",
    fontFamily: "Quicksand-Regular",
    borderRadius: "10px",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e4e8ef" },
    "&:hover fieldset": { borderColor: "#a8b4c8" },
    "&.Mui-focused fieldset": { borderColor: "#1877f2", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    fontSize: { xs: "18px", md: "20px" },
    textAlign: "center",
    fontWeight: "bold",
    letterSpacing: "0.35em",
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 11px) scale(1)",
    fontFamily: "Quicksand-Regular",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "15px", md: "16px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(24, 119, 242, 0.35)", color: "white", boxShadow: "none" },
};

// ─── Paso 5: Verificación de Código ──────────────────────────────────────────

const CodeVerification = ({ userData, onNext, onBack }) => {
  const [code, setCode]           = useState("");
  const [error, setError]         = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    setIsButtonEnabled(code.length === 4 && /^\d{4}$/.test(code));
  }, [code]);

  const handleCodeChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d{0,4}$/.test(value)) setCode(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 4 || !/^\d{4}$/.test(code)) { setError("El código debe ser de 4 dígitos"); return; }
    setIsLoading(true);
    setError("");
    try {
      const idProfesionalFantasma = leerBaul()?.idProfFantasma || null;
      const response = await callApi("/registro/crearCuenta", "post", {
        email: userData.email, telefono: userData.telefono, password: userData.password,
        rut: userData.rut, nombres: userData.nombres, apellidos: userData.apellidos,
        profesion: userData.profesion, code, link_plataforma: userData.profileUrl,
        ...(idProfesionalFantasma ? { id_profesional_fantasma: idProfesionalFantasma } : {}),
      });
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        onNext();
      } else {
        setError("Código inválido. Por favor, verifica e intenta nuevamente.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Código inválido. Por favor, verifica e intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await callApi("/registro/enviarCodigo", "post", {
        email: userData.email, telefono: userData.telefono, password: userData.password,
        rut: userData.rut, nombres: userData.nombres, apellidos: userData.apellidos, profesion: userData.profesion,
      });
      if (response?.statusCode === 200 || response?.status === 201) {
        setError("Se ha enviado un nuevo código a tu email");
        setCode("");
      } else {
        setError("Error al reenviar el código. Por favor, intenta nuevamente.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error al reenviar el código. Por favor, intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      py: { xs: 3, md: 4 },
    }}>
      <Loader isLoading={isLoading} />
      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        <Box sx={cardSx}>
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", mb: "6px" }}>
            Último paso 👌🏻
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", mb: { xs: "20px", md: "24px" }, lineHeight: 1.5 }}>
            Ingresa el código de 4 dígitos que enviamos a{" "}
            <Box component="span" sx={{ color: "#1877f2", fontFamily: "Quicksand-Bold" }}>
              {userData?.email}
            </Box>
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Código"
              value={code}
              onChange={handleCodeChange}
              margin="normal"
              required
              inputProps={{ maxLength: 4, pattern: "\\d{4}" }}
              placeholder="0000"
              sx={inputSx}
            />

            {error && (
              <Typography sx={{
                color: error.includes("enviado") ? "#16a34a" : "#d32f2f",
                fontSize: { xs: "12px", md: "13px" },
                fontFamily: "Quicksand-Regular",
                textAlign: "center",
                mt: "12px", p: "12px",
                bgcolor: error.includes("enviado") ? "rgba(22,163,74,0.07)" : "rgba(211,47,47,0.07)",
                borderRadius: "10px",
              }}>
                {error}
              </Typography>
            )}

            <Button type="submit" fullWidth variant="contained" disabled={!isButtonEnabled} sx={{ ...btnSx, mb: 1 }}>
              Empezar gratis
            </Button>

            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 1 }}>
              <Button variant="text" onClick={onBack} sx={{ color: "#6b7280", textTransform: "none", fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, p: 0 }}>
                volver
              </Button>
              <Button variant="text" onClick={handleResendCode} sx={{ color: "#1877f2", textTransform: "none", fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, p: 0 }}>
                reenviar código
              </Button>
            </Box>
          </form>
        </Box>

        <Box sx={{ mt: "12px" }}>
          <ReferralBanner />
        </Box>
      </Container>
    </Box>
  );
};

export default CodeVerification;
